import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { getUser, getUserType, State } from "../../reducers";
import { takeUntil } from "rxjs/operators";
import { SnackbarConfig, SubscribingComponent } from "../../common";

import { Statement } from "../../statements/statement";
import { RefundDialogComponent } from "../../statements/refund-dialog/refund-dialog.component";
import { SendEwrAttachmentComponent } from "../../statements/send-ewr-attachment/send-ewr-attachment.component";
import { CustomLabels } from "app/common/labels-edit/CustomLabels";
import { PackageOptions } from "app/statements/package-options";
import * as FileSaver from "file-saver";
import { ListViewAttachmentsComponent } from "../list-view-attachments/list-view-attachments.component";
import { ListViewDownloadsComponent } from "../list-view-downloads/list-view-downloads.component";
import { DownloadFileDialogComponent } from "app/common/decision-dialog/download-file-dialog.component";
import {
  MESSAGE_DOCUMENT_TYPE,
  NewMessageDialogComponent,
  ATTACHMENT_SUBTYPE
} from "app/common/send-message-dialog/send-message-dialog.component";
import { User } from "app/users";
import { MessageSenderHttpService } from "app/common/send-message-dialog/http-service/message-sender.http.service";
import { StatementService } from "app/statements/statement.service";
import { SHOW_SNACKBAR } from "app/reducers/actions/snackbar";
import { DeviceRecognizerHelper } from "app/core/helpers/device-recognizer.helper";

type FileEntry = {
  file_name: string;
  id: number;
  file_type: string;
};

@Component({
  selector: "app-list-view-item-b2b",
  templateUrl: "./list-view-item-b2b.component.html",
  styleUrls: ["./list-view-item-b2b.component.scss"],
  standalone: false
})
export class ListViewItemB2bComponent extends SubscribingComponent implements OnInit {
  private readonly rabeSupportedFromYear = 2023;
  @Input() openedItems: boolean[];
  @Input() index: number;
  @Input() statement: Statement;
  @Input() hideEditing = false;
  @Input() hideRemoving = false;
  @Input() hideDecision = false;
  @Input() hideInvoice = false;
  @Input() hidePdf = false;
  @Input() showEditButton = false;
  @Input() showCloneButton = false;
  @Input() labelList: CustomLabels[] = [];
  @Output() deleteStatement = new EventEmitter<void>();
  @Output() downloadPdf = new EventEmitter<void>();
  @Output() downloadInvoice = new EventEmitter<void>();
  @Output() showDecision = new EventEmitter<void>();
  @Output() editButton = new EventEmitter<void>();
  @Output() cloneStatement = new EventEmitter<void>();
  @Output() importStatement = new EventEmitter<void>();
  public attachmentsList: Array<FileEntry> = [];
  public rabeAttachmentsCount = 0;
  public isBusiness = false;
  public isTaxAdviser = false;
  public numberOfFilesToDownload = 0;
  public isMasterAccount = false;
  public isSubAccount = false;
  public can_send_elster_messages = false;
  private attachmentsDialogComponent = ListViewAttachmentsComponent;
  private downloadsDialogComponent = ListViewDownloadsComponent;

  public emptyCol = "-";

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private store: Store<State>,
    public messageSenderHttpService: MessageSenderHttpService,
    private statementService: StatementService
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(getUserType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((userType: string) => {
        if (userType) {
          this.isTaxAdviser = userType === "tax_adviser";
          this.isBusiness = userType === "business";
        }
      });

    this.store
      .select(getUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user: User) => {
        if (user) {
          this.isMasterAccount = user.isMasterAccount;
          this.isSubAccount = user.isSubaccount;
          this.can_send_elster_messages = user.can_send_elster_messages;
        }
      });

    this.getMessageAttachmentList();
  }

  openRefundDetailsDialog() {
    const dialogRef = this.dialog.open(RefundDialogComponent);
    dialogRef.componentInstance.statement = this.statement;
  }

  emitDownloadPdf() {
    this.downloadPdf.emit();
  }

  emitDownloadInvoice() {
    this.downloadInvoice.emit();
  }

  emitDeleteStatement() {
    this.deleteStatement.emit();
  }

  emitOnEditButton() {
    this.editButton.emit();
  }

  getRefund(): string {
    let plusSign = "";
    if (this.statement.estimatedRefund > 0) {
      plusSign = "+";
    }
    return `${plusSign}${this.statement.estimatedRefund.toFixed(2)}`;
  }

  editStatement() {
    let urlPath = "";
    let url = "";
    if (!this.isTaxAdviser) {
      if (this.isBusiness) urlPath = this.statement.isPaid ? "thank-you" : `employer/${this.statement.employers[0].id}`;
      else urlPath = this.statement.isPaid ? "thank-you" : `b2c/tax-card`;
      urlPath = this.statement.isPaid && this.statement.serviceType === PackageOptions.tax_adviser ? "finish" : urlPath;

      url = `new/statement/${this.statement.id}/${urlPath}`;
    } else {
      const employerId =
        this.statement.employers && this.statement.employers.length ? this.statement.employers[0].id : 0;
      if (this.isBusiness) urlPath = this.statement.isPaid ? `employer/${employerId}` : "dashboard";
      else urlPath = this.statement.isPaid ? `b2c/tax-card` : "dashboard";
      url = `new/statement/${this.statement.id}/${urlPath}`;
    }

    this.router.navigate([url]);
  }

  uploadEWRAttatchment() {
    if (this.statement) {
      const dialogRef = this.dialog.open(SendEwrAttachmentComponent);
      dialogRef.componentInstance.statement = this.statement;
    }
  }

  public emitCloneStatement() {
    this.cloneStatement.emit();
  }

  public emitImportStatement() {
    this.importStatement.emit();
  }

  public showDownloads() {
    const dialogRef = this.dialog.open(DownloadFileDialogComponent);
    dialogRef.componentInstance.statement = this.statement;
  }

  public openAppealDecisionDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-new-message-dialog",
      data: {
        statementData: this.statement,
        titleMaxLength: 99,
        textMaxLength: 1500,
        messageType: MESSAGE_DOCUMENT_TYPE.APPEAL,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.APPEAL.MODAL_TITLE",
          titleLabel: "SEND_MESSAGE_DIALOG.APPEAL.TITLE_LABEL",
          messageLabel: "SEND_MESSAGE_DIALOG.APPEAL.MESSAGE_LABEL",
          addFileButton: "SEND_MESSAGE_DIALOG.APPEAL.ADD_FILE_BUTTON",
          checkboxText: "SEND_MESSAGE_DIALOG.APPEAL.CHECKBOX_TEXT"
        }
      }
    });
  }

  public openSendNewMessageDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-new-message-dialog",
      data: {
        statementData: this.statement,
        titleMaxLength: 99,
        textMaxLength: 1500,
        messageType: MESSAGE_DOCUMENT_TYPE.MESSAGE,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.MESSAGE.MODAL_TITLE",
          titleLabel: "SEND_MESSAGE_DIALOG.MESSAGE.TITLE_LABEL",
          messageLabel: "SEND_MESSAGE_DIALOG.MESSAGE.MESSAGE_LABEL",
          addFileButton: "SEND_MESSAGE_DIALOG.MESSAGE.ADD_FILE_BUTTON"
        }
      }
    });
  }

  public openSendAttachmentDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.ATTACHMENT,
        subtype: "",
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.ATTACHMENT.MODAL_TITLE",
          addFileButton: "SEND_MESSAGE_DIALOG.ATTACHMENT.ADD_FILE_BUTTON"
        }
      }
    });
  }

  public openSendRabeDialogComponent() {
    const dialogRef: MatDialogRef<NewMessageDialogComponent, any> = this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.RABE,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.RABE.MODAL_TITLE",
          addFileButton: "SEND_MESSAGE_DIALOG.RABE.ADD_FILE_BUTTON",
          selectBox: {
            title: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.TITLE",
            label: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.LABEL",
            placeholder: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.PLACEHOLDER"
          },
          onResponse: {
            successTitle: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_RABE.TITLE",
            successMessage: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_RABE.REQUEST"
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getMessageAttachmentList();
    });
  }

  public openRabeAttachmentListDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.RABE,
        previewOnlyRabeMode: true,
        text: {
          modalTitle: "DASHBOARD.ATTACHMENT.TITLE",
          selectBox: {
            title: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.TITLE",
            label: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.LABEL",
            placeholder: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.PLACEHOLDER"
          }
        }
      }
    });
  }

  public openRabeDialogFromCloudBadge(): void {
    if (!this.isRabeEnabledForStatement()) {
      return;
    }

    if (this.statement.isSent()) {
      this.openRabeAttachmentListDialogComponent();
      return;
    }

    this.openSendRabeDialogComponent();
  }

  public openSendBellegeDialogComponent() {
    const dialogRef: MatDialogRef<NewMessageDialogComponent, any> = this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.ATTACHMENT,
        subtype: ATTACHMENT_SUBTYPE.BELEGE,
        onlySave: true,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.ATTACHMENT.BELLEGE_TITLE",
          addFileButton: "SEND_MESSAGE_DIALOG.ATTACHMENT.ADD_FILE_BUTTON",
          sendByEmailText: "SEND_MESSAGE_DIALOG.ATTACHMENT.SEND_BY_EMAIL",
          titleTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.SEND_BELEGE.TITLE",
          messageTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.SEND_BELEGE.MESSAGE",
          onResponse: {
            successTitle: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_BELEGE.TITLE",
            successMessage: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_BELEGE.REQUEST"
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getMessageAttachmentList();
    });
  }

  public openSendVollmachtDialogComponent() {
    const dialogRef: MatDialogRef<NewMessageDialogComponent, any> = this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.ATTACHMENT,
        subtype: ATTACHMENT_SUBTYPE.VOLMACHT,
        onlySave: true,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.ATTACHMENT.VOLMACHT_TITLE",
          addFileButton: "SEND_MESSAGE_DIALOG.ATTACHMENT.ADD_FILE_BUTTON",
          titleTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.SEND_VOLLMACHT.TITLE",
          messageTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.SEND_VOLLMACHT.MESSAGE",
          onResponse: {
            successTitle: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_VOLLMACHT.TITLE",
            successMessage: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_VOLLMACHT.REQUEST"
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getMessageAttachmentList();
    });
  }

  public displayMessageButtons(): boolean {
    if (this.isMasterAccount) return this.statement.isSent();

    return this.statement.isSent() && this.can_send_elster_messages;
  }

  public displayRabeButton(): boolean {
    return !this.statement.isSent() && this.can_send_elster_messages && this.isRabeEnabledForStatement();
  }

  public displayMessageBellege(): boolean {
    if (this.isMasterAccount) return !this.statement.isSent();

    return !this.statement.isSent() && this.can_send_elster_messages;
  }

  get shouldShowHamburgerMenu(): boolean {
    return this.displayMessageButtons() || this.displayMessageBellege() || this.isMasterAccount || this.isSubAccount;
  }

  get shouldShowRabeDocumentsBadge(): boolean {
    if (!this.statement) return false;
    if (!this.can_send_elster_messages) return false;
    if (!this.isRabeEnabledForStatement()) return false;
    if (this.statement.isSent()) return this.rabeAttachmentsCount > 0;
    return true;
  }

  public isRabeEnabledForStatement(): boolean {
    const statementYear = Number(this.statement?.fullYear);
    if (!Number.isFinite(statementYear)) return true;

    return statementYear >= this.rabeSupportedFromYear;
  }

  public getMessageAttachmentList() {
    this.messageSenderHttpService.getMessageAttachmentList(this.statement.id).subscribe({
      next: (response: any) => {
        const attachmentMessages = Array.isArray(response) ? response : [];

        const rabeMessages = attachmentMessages.filter(
          (attachment) => String(attachment?.type || "").toLowerCase() === MESSAGE_DOCUMENT_TYPE.RABE.toLowerCase()
        );
        this.rabeAttachmentsCount = rabeMessages.reduce((count, attachment) => {
          return count + (Array.isArray(attachment?.files) ? attachment.files.length : 0);
        }, 0);

        const filteredAttachments = attachmentMessages.filter((attachment) =>
          [ATTACHMENT_SUBTYPE.BELEGE, ATTACHMENT_SUBTYPE.VOLMACHT].includes(attachment.subtype)
        );

        const attachments: Array<FileEntry> = [];

        filteredAttachments.forEach((attachment) => {
          if (attachment?.files?.length === 0) return;

          const fileType =
            attachment.subtype === ATTACHMENT_SUBTYPE.BELEGE
              ? "COMMON.BELLEGE_ATTACHMENT_LIST_NAME"
              : "COMMON.VOLLMACHT_ATTACHMENT";

          attachment.files.forEach((file) => {
            attachments.push({
              file_name: file.file || "file.pdf",
              id: file.id,
              file_type: fileType
            });
          });
        });

        this.attachmentsList = [...attachments];
      },
      error: (error) => {
        this.rabeAttachmentsCount = 0;
        console.error(error);
      }
    });
  }

  public downloadAttachment(index: number) {
    const { file_name, id } = this.attachmentsList[index];

    this.statementService.downloadDecisionPDF_desktop(this.statement.id, "message_document", id).subscribe({
      next: (res: Blob) => {
        this.saveFile(res, file_name);
      },
      error: (error: any) => {
        this.showPDFError();
        console.error(error);
      }
    });
  }

  private saveFile = (response: Blob, file_name: string) => {
    const blob = response;
    FileSaver.saveAs(blob, file_name);
  };

  showPDFError() {
    this.store.dispatch({ type: SHOW_SNACKBAR, payload: new SnackbarConfig("SNACKBAR.PDF_NOT_AVAILABLE", "ERROR") });
  }
}
